import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import {
  EntityIdAndType,
  EntityState,
  EntityType,
  EntityTypeEnum,
  Organisation,
  OrganisationType,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  ValidationIssue,
  WorkflowActions
} from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Tabs } from 'view/components/tabs'
import { OrganisationDetailsPanel } from './OrganisationDetailsPanel'
import { PrimeBrokersPanel } from './PrimeBrokersPanel'
import { TradeSplitPanel } from './TradeSplitPanel'
import { SalesCoveragePanel } from './SalesCoveragePanel'
import {
  createWorkflowReferenceDataAvailableEventsSelector,
  createWorkflowReferenceDataEntitySelector,
  createWorkflowReferenceDataValidationIssuesSelector
} from 'selectors/workflowSelectors'
import { Locale, Translation } from 'locale'
import { createPlatformMarketStatusClosedSelector } from 'selectors/platformSelectors'
import { GfidPanelComponent } from './GfidPanel'

export type OrganisationDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  organisationEntityId: string
  onClose?: () => unknown
}

interface StateProps {
  organisationEntity: ReferenceDataEntityType<Organisation> | null
  availableEvents: ReferenceDataManagementEvent[]
  workflowValidationIssues: ValidationIssue[]
  marketStatusClosed: boolean
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: Organisation; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: OrganisationDialogProps) => ownProps.workflowId
const organisationEntityIdSelector = (_state: {}, ownProps: OrganisationDialogProps) => ownProps.organisationEntityId

const mapStateToProps = createStructuredSelector({
  organisationEntity: createWorkflowReferenceDataEntitySelector(EntityType.ORGANISATION, workflowIdSelector, organisationEntityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  workflowValidationIssues: createWorkflowReferenceDataValidationIssuesSelector(workflowIdSelector),
  marketStatusClosed: createPlatformMarketStatusClosedSelector()
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  width: 1000px;
  min-height: 450px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
`

export const OrganisationDialogComponent: React.FunctionComponent<OrganisationDialogProps> = props => {
  const {
    workflowId,
    organisationEntity,
    availableEvents,
    workflowValidationIssues,
    marketStatusClosed,
    onUpdate,
    onSubmitWorkflow,
    onCancelWorkflow,
    onClose
  } = props

  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_ORGANISATION_DETAILS)

  if (!organisationEntity) {
    return null
  }

  const organisation = organisationEntity.data
  const isNewOrganisation = organisationEntity.entityState === EntityState.NEW
  const title = isNewOrganisation ? Locale.translate(Translation.TC_NEW_ORGANISATION) : organisation.legalName
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const validationIssues = organisationEntity.validationIssues ?? []
  const hasValidationIssues = validationIssues.length > 0 || workflowValidationIssues.length > 0

  const getTabs = () => {
    const tabs = [Translation.TC_ORGANISATION_DETAILS]

    if (!isNewOrganisation) {
      tabs.push(Translation.TC_ORGANISATION_PRIME_BROKERS)
      tabs.push(Translation.TC_GFID)
    }
    if (!isNewOrganisation && organisation.organisationTypes.includes(OrganisationType.BUY_SIDE)) {
      tabs.push(Translation.TC_ORGANISATION_TRADE_SPLITS)
    }
    if (!isNewOrganisation && organisation.organisationTypes.includes(OrganisationType.SELL_SIDE)) {
      tabs.push(Translation.TC_ORGANISATION_SALES_COVERAGE)
    }

    return tabs
  }

  const handleChangeTab = (tab: Translation) => {
    setSelectedTab(tab)
  }

  const handleUpdateOrganisation = (partialOrganisation: Partial<Organisation>) => {
    onUpdate({
      entityId: organisationEntity.id,
      entityType: EntityTypeEnum.ORGANISATION,
      updatedEntity: {
        ...organisation,
        ...partialOrganisation
      },
      workflowId
    })
  }

  const handleSave = () => {
    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={title}>
      <Tabs selectedTab={selectedTab} onChangeTab={handleChangeTab} tabs={getTabs()} />
      <Content>
        {selectedTab === Translation.TC_ORGANISATION_DETAILS && (
          <OrganisationDetailsPanel
            organisation={organisation}
            validationIssues={validationIssues}
            readOnly={readOnly}
            onUpdate={handleUpdateOrganisation}
            marketStatusClosed={marketStatusClosed}
          />
        )}
        {selectedTab === Translation.TC_GFID && (
          <GfidPanelComponent
            organisation={organisation}
            onUpdate={handleUpdateOrganisation}
            validationIssues={validationIssues}
            readOnly={false}
          />
        )}
        {selectedTab === Translation.TC_ORGANISATION_PRIME_BROKERS && (
          <PrimeBrokersPanel workflowId={workflowId} organisationId={organisation.id} readOnly={readOnly} />
        )}
        {selectedTab === Translation.TC_ORGANISATION_TRADE_SPLITS && (
          <TradeSplitPanel workflowId={workflowId} organisationId={organisation.id} />
        )}
        {selectedTab === Translation.TC_ORGANISATION_SALES_COVERAGE && <SalesCoveragePanel organisationId={organisation.id} />}
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'OrganisationDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'OrganisationDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={handleSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const OrganisationDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(OrganisationDialogComponent)
